/**
 * Read a query parameter from either side of the hash.
 *
 * The app uses HashRouter, so a link a person actually types —
 * `/#/radar?key=abc` — puts the parameter inside `location.hash`, where
 * `location.search` cannot see it. Anything that reads only `search` works when
 * the parameter is placed before the `#` and silently fails otherwise, which is
 * the worst kind of broken: it looks like the value was wrong.
 *
 * Checks the real query string first, then the query portion of the hash.
 */
export function readUrlParam(name: string): string | null {
    if (typeof window === 'undefined') return null;
    try {
        const fromSearch = new URLSearchParams(window.location.search).get(name);
        if (fromSearch) return fromSearch;

        const hash = window.location.hash;
        const q = hash.indexOf('?');
        if (q === -1) return null;
        return new URLSearchParams(hash.slice(q + 1)).get(name);
    } catch {
        return null;
    }
}

/**
 * Remove a parameter from wherever it was found and rewrite the URL in place,
 * so a secret does not sit in history or in a link that gets shared onward.
 */
export function stripUrlParam(name: string): void {
    if (typeof window === 'undefined') return;
    try {
        const url = new URL(window.location.href);
        let changed = false;

        if (url.searchParams.has(name)) {
            url.searchParams.delete(name);
            changed = true;
        }

        const q = url.hash.indexOf('?');
        if (q !== -1) {
            const params = new URLSearchParams(url.hash.slice(q + 1));
            if (params.has(name)) {
                params.delete(name);
                const rest = params.toString();
                url.hash = url.hash.slice(0, q) + (rest ? `?${rest}` : '');
                changed = true;
            }
        }

        if (changed) {
            window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        }
    } catch { /* history blocked — the key still works, it just stays in the URL */ }
}
